/**
 * TeamPage - Gestao de Equipe (Enterprise Final)
 *
 * Painel de gestao ativa: identificacao de operadores, status inteligente,
 * gargalo qualitativo, SLA dual, capacidade, destaque critico, acoes por operador.
 * Ribbon padronizado: nav esquerda, filtros direita.
 */

import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronDown,
  ChevronRight,
  Eye,
  FlaskConical,
  ListFilter,
  Minus,
  Plus,
  Search,
  Shuffle,
  Users,
  X,
} from 'lucide-react';
import { organizationStructureService } from '../services/admin/organizationStructureService';
import { getOrgWipMaxByUser, getOrgCargoNameByUser } from '../services/capacidadeService';
import { ModuleLayout } from '../components/layout/ModuleLayout';

import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { UserRow } from '../services/admin/organizationStructureService';
import { desktopRibbonInputClass } from '../components/ribbon/DesktopRibbonControls';
import { useCapacidadeRibbon } from '../hooks/useCapacidadeRibbon';
import {
  fetchTeamWorkItems,
  computeTeamWorkload,
  computeTeamDashboard,
} from '../modules/minha-mesa/services/unifiedWorkService';
import { simulateRebalance, executeRebalance } from '../modules/minha-mesa/services/rebalanceService';
import type { RebalancePlan } from '../modules/minha-mesa/services/rebalanceService';
import { RebalanceModal } from '../modules/minha-mesa/components/RebalanceModal';

// ── Simulation Drawer ────────────────────────────────────────

interface SimProjection {
  occupationPct: number;
  overloadedCount: number;
  recommendedOps: number;
}

function calcSimProjection(
  currentWorkload: number,
  operators: number,
  capacityPerOp: number,
  demandMultiplier: number,
): SimProjection {
  const projectedLoad = Math.round(currentWorkload * demandMultiplier);
  const totalCapacity = operators * capacityPerOp;
  const occupationPct = totalCapacity > 0 ? Math.round((projectedLoad / totalCapacity) * 100) : 0;
  const overloadedCount = operators > 0 ? Math.max(0, Math.ceil((projectedLoad - totalCapacity) / capacityPerOp)) : 0;
  const recommendedOps = totalCapacity > 0 ? Math.ceil(projectedLoad / capacityPerOp) : operators;
  return { occupationPct, overloadedCount, recommendedOps };
}

function SimulacaoDrawer({
  members,
  currentWorkload,
  onClose,
}: {
  members: TeamMemberWorkload[];
  currentWorkload: number;
  onClose: () => void;
}) {
  const baseOperators = Math.max(1, members.length);
  const [operators, setOperators] = useState(baseOperators);
  const [capacityPerOp, setCapacityPerOp] = useState(CAPACITY_PER_OPERATOR);
  const [demandMult, setDemandMult] = useState(1.0);

  const proj = useMemo(
    () => calcSimProjection(currentWorkload, operators, capacityPerOp, demandMult),
    [currentWorkload, operators, capacityPerOp, demandMult],
  );

  const projColor = proj.occupationPct > 100
    ? 'text-red-600'
    : proj.occupationPct >= 80
    ? 'text-emerald-600'
    : 'text-blue-600';

  const recommendation = (() => {
    if (proj.occupationPct > 110) return `Adicionar ${proj.recommendedOps - operators} operadores para absorver a demanda.`;
    if (proj.occupationPct > 100) return `Sobrecarga de ${proj.occupationPct - 100}%. Redistribuir ou aumentar capacidade.`;
    if (proj.occupationPct >= 80) return 'Equipe próximo ao limite ideal. Monitorar.';
    return 'Capacidade disponível. Pode absorver mais demanda.';
  })();

  function Counter({
    label, value, min, max, step = 1, onDec, onInc, display,
  }: {
    label: string; value: number; min: number; max: number; step?: number;
    onDec: () => void; onInc: () => void; display?: string;
  }) {
    return (
      <div className="flex items-center justify-between py-2 border-b border-[var(--border-default)]">
        <span className="text-[11px] text-[var(--text-secondary)]">{label}</span>
        <div className="flex items-center gap-2">
          <button onClick={onDec} disabled={value <= min}
            className="flex h-6 w-6 items-center justify-center rounded-sm border border-[var(--border-default)] hover:bg-[var(--button-hover)] disabled:opacity-30">
            <Minus size={10} />
          </button>
          <span className="w-[44px] text-center text-[12px] font-bold tabular-nums text-[var(--text-primary)]">
            {display ?? value}
          </span>
          <button onClick={onInc} disabled={value >= max}
            className="flex h-6 w-6 items-center justify-center rounded-sm border border-[var(--border-default)] hover:bg-[var(--button-hover)] disabled:opacity-30">
            <Plus size={10} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed right-0 top-0 z-[200] flex h-full w-[320px] flex-col border-l border-[var(--border-default)] bg-[var(--bg-surface)] shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[var(--border-default)] bg-[var(--bg-elevated)] px-4 py-3">
        <div className="flex items-center gap-2">
          <FlaskConical size={14} className="text-blue-500" />
          <span className="text-[12px] font-semibold text-[var(--text-primary)]">Simulação What-If</span>
        </div>
        <button onClick={onClose} className="rounded-sm p-1 hover:bg-[var(--button-hover)]">
          <X size={14} className="text-[var(--text-muted)]" />
        </button>
      </div>

      {/* Variables */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <p className="mb-3 text-[10px] uppercase tracking-wider font-semibold text-[var(--text-muted)]">Variáveis de Carga</p>
        <Counter
          label="Operadores disponíveis"
          value={operators}
          min={1} max={baseOperators + 10}
          onDec={() => setOperators(v => Math.max(1, v - 1))}
          onInc={() => setOperators(v => Math.min(baseOperators + 10, v + 1))}
        />
        <Counter
          label="Capacidade por operador (tarefas)"
          value={capacityPerOp}
          min={1} max={20}
          onDec={() => setCapacityPerOp(v => Math.max(1, v - 1))}
          onInc={() => setCapacityPerOp(v => Math.min(20, v + 1))}
        />
        <Counter
          label="Multiplicador de demanda"
          value={demandMult}
          min={0.5} max={3.0} step={0.1}
          display={`×${demandMult.toFixed(1)}`}
          onDec={() => setDemandMult(v => Math.max(0.5, Math.round((v - 0.1) * 10) / 10))}
          onInc={() => setDemandMult(v => Math.min(3.0, Math.round((v + 0.1) * 10) / 10))}
        />

        {/* Projection */}
        <p className="mt-5 mb-3 text-[10px] uppercase tracking-wider font-semibold text-[var(--text-muted)]">Cenário Projetado</p>
        <div className="rounded-sm border border-[var(--border-default)] bg-[var(--bg-canvas)] p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-[var(--text-secondary)]">Ocupação projetada</span>
            <span className={`text-[18px] font-bold tabular-nums ${projColor}`}>{proj.occupationPct}%</span>
          </div>
          <div className="h-1.5 w-full rounded-sm bg-[var(--bg-elevated)] overflow-hidden">
            <div
              className={`h-full rounded-sm transition-all ${
                proj.occupationPct > 100 ? 'bg-red-500'
                : proj.occupationPct >= 80 ? 'bg-emerald-500'
                : 'bg-blue-500'
              }`}
              style={{ width: `${Math.min(proj.occupationPct, 100)}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-[var(--text-muted)]">Ops sobrecarregados</span>
            <span className={`font-semibold ${
              proj.overloadedCount > 0 ? 'text-red-600' : 'text-emerald-600'
            }`}>
              {proj.overloadedCount > 0 ? proj.overloadedCount : '—'}
            </span>
          </div>
          {proj.recommendedOps !== operators && (
            <div className="flex items-center justify-between text-[10px]">
              <span className="text-[var(--text-muted)]">Operadores recomendados</span>
              <span className="font-semibold text-[var(--text-primary)]">{proj.recommendedOps}</span>
            </div>
          )}
        </div>

        <div className="mt-3 rounded-sm border border-[var(--border-default)] bg-[var(--bg-elevated)] px-3 py-2">
          <p className="text-[10px] text-[var(--text-secondary)] leading-relaxed">{recommendation}</p>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-[var(--border-default)] px-4 py-3">
        <button
          onClick={() => { setOperators(baseOperators); setCapacityPerOp(CAPACITY_PER_OPERATOR); setDemandMult(1.0); }}
          className="w-full rounded-sm border border-[var(--border-default)] py-1.5 text-[11px] text-[var(--text-secondary)] hover:bg-[var(--button-hover)]"
        >
          Resetar variáveis
        </button>
      </div>
    </div>
  );
}
import type {
  UnifiedWorkItem,
  TeamMemberWorkload,
  TeamDashboardKPIs,
  LoadStatus,
  WorkItemSource,
} from '../modules/minha-mesa/types/workItem';
import {
  WORK_STATUS_LABELS,
  WORK_STATUS_COLORS,
  WORK_SOURCE_COLORS,
  WORK_SOURCE_LABELS,
} from '../modules/minha-mesa/types/workItem';

// ── Constants ────────────────────────────────────────────────

const CAPACITY_PER_OPERATOR = 8;

type TeamSortColumn =
  | 'name'
  | 'status'
  | 'in_progress'
  | 'pending'
  | 'overdue'
  | 'completed'
  | 'occupation'
  | 'sla'
  | 'bottleneck';

type SortDirection = 'asc' | 'desc';

const STATUS_ORDER: Record<LoadStatus, number> = { normal: 0, high: 1, overloaded: 2 };

function memberSortValue(m: TeamMemberWorkload, col: TeamSortColumn): number | string {
  switch (col) {
    case 'name': return m.user_name.toLowerCase();
    case 'status': return STATUS_ORDER[m.load_status] ?? 0;
    case 'in_progress': return m.tasks_in_progress;
    case 'pending': return m.tasks_pending;
    case 'overdue': return m.tasks_overdue;
    case 'completed': return m.tasks_completed_today;
    case 'occupation': return m.total_active;
    case 'sla': return m.sla_violated_pct;
    case 'bottleneck': return m.bottleneck_score;
  }
}

function sortMembersByColumn(
  members: TeamMemberWorkload[],
  col: TeamSortColumn,
  direction: SortDirection,
): TeamMemberWorkload[] {
  const mult = direction === 'asc' ? 1 : -1;
  return [...members].sort((a, b) => {
    const va = memberSortValue(a, col);
    const vb = memberSortValue(b, col);
    if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * mult;
    return ((va as number) - (vb as number)) * mult;
  });
}

type TypeFilter = 'all' | WorkItemSource;

const TYPE_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'action', label: 'Acao' },
  { value: 'workflow', label: 'Workflow' },
  { value: 'problem', label: 'Problema' },
  { value: 'monitoring', label: 'Monitoramento' },
];

function sortMembers(members: TeamMemberWorkload[], col: TeamSortColumn, dir: SortDirection): TeamMemberWorkload[] {
  return sortMembersByColumn(members, col, dir);
}

// ── Role labels ──────────────────────────────────────────────

const ROLE_DISPLAY: Record<string, string> = {
  owner: 'Owner',
  consultor_master: 'Owner',
  admin: 'Administrador',
  consultant: 'Consultor',
  manager: 'Gestor',
  operator: 'Operador',
  client: 'Cliente',
};

// ── Status inteligente ───────────────────────────────────────

const STATUS_LABELS: Record<LoadStatus, string> = {
  normal: 'Normal',
  high: 'Atenção',
  overloaded: 'Sobrecarga',
};

const STATUS_STYLES: Record<LoadStatus, { bg: string; text: string; ring: string }> = {
  normal: { bg: 'bg-emerald-100 dark:bg-emerald-500/15', text: 'text-emerald-700 dark:text-emerald-300', ring: '' },
  high: { bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-300', ring: '' },
  overloaded: { bg: 'bg-red-100 dark:bg-red-500/15', text: 'text-red-700 dark:text-red-300', ring: 'ring-1 ring-red-300 dark:ring-red-500/30' },
};

// ── Gargalo qualitativo ──────────────────────────────────────

function bottleneckLabel(score: number): { text: string; color: string } {
  if (score > 10) return { text: 'Alto', color: 'text-red-600 dark:text-red-400' };
  if (score > 5) return { text: 'Médio', color: 'text-amber-600 dark:text-amber-400' };
  if (score > 0) return { text: 'Baixo', color: 'text-emerald-600 dark:text-emerald-400' };
  return { text: '--', color: 'text-[var(--text-muted)]' };
}

// ── Avatar/Iniciais ──────────────────────────────────────────

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] ?? '?').toUpperCase();
}

function isCritical(m: TeamMemberWorkload): boolean {
  return m.load_status === 'overloaded' || m.tasks_overdue >= 3 || m.sla_violated_pct > 30;
}

// ── KPI filter type ──────────────────────────────────────────
type KpiFilter = null | 'overdue' | 'critical';

// ── KPI Cards ────────────────────────────────────────────────

function TeamDashboardCards({
  kpis,
  members,
  activeFilter,
  onFilterClick,
}: {
  kpis: TeamDashboardKPIs;
  members: TeamMemberWorkload[];
  activeFilter: KpiFilter;
  onFilterClick: (filter: KpiFilter) => void;
}) {
  const fmt = (v: number | null) => v === null ? '--' : v.toFixed(1);
  const totalCapacity = members.length * CAPACITY_PER_OPERATOR;
  const occupationPct = totalCapacity > 0 ? Math.round((kpis.current_workload / totalCapacity) * 100) : 0;
  const criticalCount = members.filter(isCritical).length;
  const slaOkPct = kpis.avg_sla_compliance_pct;
  const slaViolPct = 100 - slaOkPct;

  // Gargalo Ativo — operador com maior bottleneck_score
  const topBottleneck = members.length > 0
    ? [...members].sort((a, b) => b.bottleneck_score - a.bottleneck_score)[0]
    : null;
  const hasBottleneck = topBottleneck !== null && topBottleneck.bottleneck_score > 0;
  const topBottleneckLabel = hasBottleneck
    ? topBottleneck!.user_name.split(' ')[0]
    : 'Balanceado';

  // Capacidade: cor dinâmica
  const capacidadeColor = occupationPct > 100
    ? 'text-red-600 dark:text-red-400'
    : occupationPct > 90
    ? 'text-orange-500 dark:text-orange-400'
    : occupationPct > 60
    ? 'text-amber-600 dark:text-amber-400'
    : 'text-emerald-600 dark:text-emerald-400';

  const cards: {
    key: string;
    label: string;
    value: string;
    sub?: string;
    valueColor: string;
    labelColor: string;
    bgClass: string;
    alert?: boolean;
    clickFilter?: KpiFilter;
  }[] = [
    {
      key: 'tasks',
      label: 'Tarefas/Operador',
      value: fmt(kpis.tasks_per_employee),
      sub: `${kpis.total_members} operadores`,
      valueColor: 'text-[var(--text-primary)]',
      labelColor: 'text-[var(--text-muted)]',
      bgClass: 'bg-[var(--bg-surface)]',
    },
    {
      key: 'overdue',
      label: 'Atrasadas',
      value: String(kpis.overdue_tasks),
      sub: kpis.overdue_tasks > 0 ? 'Requer atenção' : 'Nenhuma',
      valueColor: kpis.overdue_tasks > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400',
      labelColor: kpis.overdue_tasks > 0 ? 'text-red-500 dark:text-red-400' : 'text-[var(--text-muted)]',
      bgClass: kpis.overdue_tasks > 0 ? 'bg-red-50/60 dark:bg-red-500/10' : 'bg-[var(--bg-surface)]',
      clickFilter: 'overdue',
    },
    {
      key: 'capacity',
      label: 'Capacidade',
      value: `${occupationPct}%`,
      sub: `${kpis.current_workload} / ${totalCapacity} tarefas`,
      valueColor: capacidadeColor,
      labelColor: occupationPct > 90 ? 'text-orange-500 dark:text-orange-400' : 'text-[var(--text-muted)]',
      bgClass: occupationPct > 100
        ? 'bg-red-50/60 dark:bg-red-500/10'
        : occupationPct > 90
        ? 'bg-orange-50/60 dark:bg-orange-500/10'
        : 'bg-[var(--bg-surface)]',
      alert: occupationPct > 100,
    },
    {
      key: 'bottleneck',
      label: 'Gargalo Ativo',
      value: topBottleneckLabel,
      sub: hasBottleneck
        ? `Score ${topBottleneck!.bottleneck_score} · ${topBottleneck!.tasks_overdue} atrasada(s)`
        : 'Fluxo Balanceado',
      valueColor: hasBottleneck && topBottleneck!.bottleneck_score > 5
        ? 'text-amber-600 dark:text-amber-400'
        : hasBottleneck
        ? 'text-[var(--text-primary)]'
        : 'text-emerald-600 dark:text-emerald-400',
      labelColor: hasBottleneck && topBottleneck!.bottleneck_score > 5 ? 'text-amber-500 dark:text-amber-400' : 'text-[var(--text-muted)]',
      bgClass: hasBottleneck && topBottleneck!.bottleneck_score > 5
        ? 'bg-amber-50/60 dark:bg-amber-500/10'
        : hasBottleneck
        ? 'bg-[var(--bg-surface)]'
        : 'bg-emerald-50/40 dark:bg-emerald-500/10',
    },
    {
      key: 'sla',
      label: 'SLA Equipe',
      value: `${slaOkPct}%`,
      sub: slaViolPct > 0 ? `${slaViolPct}% violado` : 'Conformidade total',
      valueColor: slaOkPct >= 90
        ? 'text-emerald-600 dark:text-emerald-400'
        : slaOkPct >= 70
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-red-600 dark:text-red-400',
      labelColor: 'text-[var(--text-muted)]',
      bgClass: slaOkPct >= 90
        ? 'bg-[var(--bg-surface)]'
        : slaOkPct >= 70
        ? 'bg-amber-50/60 dark:bg-amber-500/10'
        : 'bg-red-50/60 dark:bg-red-500/10',
    },
    {
      key: 'critical',
      label: 'Em Risco',
      value: String(criticalCount),
      sub: criticalCount > 0 ? 'operadores críticos' : 'Equipe saudável',
      valueColor: criticalCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400',
      labelColor: criticalCount > 0 ? 'text-red-500 dark:text-red-400' : 'text-[var(--text-muted)]',
      bgClass: criticalCount > 0 ? 'bg-red-50/60 dark:bg-red-500/10' : 'bg-[var(--bg-surface)]',
      clickFilter: 'critical',
    },
  ];

  return (
    <div className="grid grid-cols-6 gap-2 py-3">
      {cards.map((card) => {
        const isClickable = card.clickFilter !== undefined;
        const isActive = card.clickFilter !== undefined && activeFilter === card.clickFilter;
        return (
          <button
            key={card.key}
            type="button"
            disabled={!isClickable}
            onClick={() => isClickable && onFilterClick(isActive ? null : card.clickFilter!)}
            className={`${card.bgClass} rounded-sm px-3 py-2.5 border text-left transition-all ${
              isActive
                ? 'border-blue-400 ring-1 ring-blue-300/40 dark:border-blue-500/60 dark:ring-blue-400/30'
                : 'border-[var(--border-default)]'
            } ${isClickable ? 'cursor-pointer hover:shadow-sm' : 'cursor-default'}`}
          >
            <span className={`text-[9px] font-semibold uppercase tracking-wider ${card.labelColor}`}>{card.label}</span>
            <div className={`mt-0.5 flex items-center gap-1 text-xl font-bold tabular-nums ${card.valueColor}`}>
              {card.alert && <AlertTriangle size={14} className="shrink-0" />}
              {card.value}
            </div>
            {card.sub && <p className={`mt-0.5 text-[9px] truncate ${card.labelColor}`}>{card.sub}</p>}
          </button>
        );
      })}
    </div>
  );
}

// ── Employee Table ───────────────────────────────────────────

function EmployeeTable({
  members,
  selectedUserId,
  onSelectUser,
  sortColumn,
  sortDirection,
  onSortChange,
}: {
  members: TeamMemberWorkload[];
  selectedUserId: string | null;
  onSelectUser: (userId: string) => void;
  sortColumn: TeamSortColumn;
  sortDirection: SortDirection;
  onSortChange: (col: TeamSortColumn) => void;
}) {
  const SortableTh = ({
    col,
    label,
    className,
    align = 'center',
  }: {
    col: TeamSortColumn;
    label: string;
    className?: string;
    align?: 'left' | 'center';
  }) => {
    const active = sortColumn === col;
    const Icon = active ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <th className={`px-3 py-2 font-medium ${align === 'center' ? 'text-center' : ''} ${className ?? ''}`}>
        <button
          type="button"
          onClick={() => onSortChange(col)}
          className={`inline-flex items-center gap-1 ${align === 'center' ? 'justify-center' : ''} w-full select-none transition-colors ${
            active ? 'text-[var(--text-primary)]' : 'hover:text-[var(--text-primary)]'
          }`}
        >
          <span>{label}</span>
          <Icon size={10} className={active ? 'opacity-90' : 'opacity-40'} />
        </button>
      </th>
    );
  };

  return (
    <div className="enterprise-table-shell overflow-x-auto">
      <table className="min-w-full table-fixed text-[11px]">
        <thead className="enterprise-table-header text-left font-mono text-[10px] uppercase tracking-[0.14em]">
          <tr>
            <SortableTh col="name" label="Operador" align="left" className="w-[220px]" />
            <SortableTh col="status" label="Status" className="w-[90px]" />
            <SortableTh col="in_progress" label="Andamento" />
            <SortableTh col="pending" label="Pendentes" />
            <SortableTh col="overdue" label="Atrasadas" />
            <SortableTh col="completed" label="Concl." />
            <SortableTh col="occupation" label="Ocupação" className="w-[120px]" />
            <SortableTh col="sla" label="SLA" className="w-[90px]" />
            <SortableTh col="bottleneck" label="Gargalo" className="w-[120px]" />
          </tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const isSelected = selectedUserId === member.user_id;
            const critical = isCritical(member);
            const status = STATUS_STYLES[member.load_status];
            const bn = bottleneckLabel(member.bottleneck_score);
            const occupationPct = Math.round((member.total_active / CAPACITY_PER_OPERATOR) * 100);
            const slaOkPct = 100 - member.sla_violated_pct;

            // Gargalo: texto de diagnóstico
            const bottleneckDiagnosis = (() => {
              if (member.tasks_overdue >= 3) return 'Fila atrasada';
              if (member.sla_violated_pct > 30) return 'SLA em risco';
              if (occupationPct > 100) return 'Sobrecarga';
              if (member.bottleneck_score > 5) return 'Fluxo travado';
              return '--';
            })();

            return (
              <tr
                key={member.user_id}
                onClick={() => onSelectUser(member.user_id)}
                className={`enterprise-table-row cursor-pointer transition-colors ${
                  critical && !isSelected ? 'border-l-2 border-l-red-400 dark:border-l-red-500/70' : ''
                } ${
                  isSelected
                    ? 'is-selected bg-blue-50/60 dark:bg-blue-500/10'
                    : 'hover:bg-[var(--button-hover)]'
                }`}
              >
                <td className="px-3 py-2 align-middle">
                  <div className="flex items-center gap-2.5">
                    {isSelected ? <ChevronDown size={12} /> : <ChevronRight size={12} className="text-[var(--text-muted)]" />}
                    <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-[10px] font-bold ${
                      critical
                        ? 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300'
                        : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]'
                    }`}>
                      {getInitials(member.user_name)}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-[var(--text-primary)] truncate leading-tight">{member.user_name}</div>
                      <div className="text-[9px] text-[var(--text-muted)] truncate">{member.role_label ?? '—'}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-center align-middle">
                  <span className={`inline-block rounded-sm px-1.5 py-0.5 text-[9px] font-bold ${status.bg} ${status.text} ${status.ring}`}>
                    {STATUS_LABELS[member.load_status]}
                  </span>
                </td>
                <td className="px-3 py-2 text-center align-middle font-mono">
                  <span className="font-semibold text-blue-600 dark:text-blue-400">{member.tasks_in_progress}</span>
                </td>
                <td className="px-3 py-2 text-center align-middle font-mono">
                  <span className="font-semibold text-amber-600 dark:text-amber-400">{member.tasks_pending}</span>
                </td>
                <td className="px-3 py-2 text-center align-middle font-mono">
                  <span className={`font-semibold ${member.tasks_overdue > 0 ? 'text-red-600 dark:text-red-400' : 'text-[var(--text-muted)]'}`}>
                    {member.tasks_overdue || '--'}
                  </span>
                </td>
                <td className="px-3 py-2 text-center align-middle font-mono">
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">{member.tasks_completed_today}</span>
                </td>
                {/* Ocupação — Progress Bar + % */}
                <td className="px-3 py-2 align-middle">
                  <div className="flex flex-col items-center gap-1">
                    <span className={`text-[10px] font-bold tabular-nums ${
                      occupationPct > 100 ? 'text-red-600 dark:text-red-400'
                      : occupationPct >= 80 ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-blue-600 dark:text-blue-400'
                    }`}>{occupationPct}%</span>
                    <div className="h-[3px] w-full max-w-[72px] rounded-sm bg-[var(--bg-elevated)] overflow-hidden">
                      <div
                        className={`h-full rounded-sm transition-all ${
                          occupationPct > 100 ? 'bg-red-500'
                          : occupationPct >= 80 ? 'bg-emerald-500'
                          : 'bg-blue-500'
                        }`}
                        style={{ width: `${Math.min(occupationPct, 100)}%` }}
                      />
                    </div>
                    <span className="text-[9px] text-[var(--text-muted)] tabular-nums">{member.total_active}/{CAPACITY_PER_OPERATOR}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-center align-middle">
                  {member.sla_violated_pct > 0 ? (
                    <div className="flex flex-col items-center leading-tight">
                      <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">{slaOkPct}% OK</span>
                      <span className="text-[9px] font-semibold text-red-600 dark:text-red-400">{member.sla_violated_pct}% viol.</span>
                    </div>
                  ) : (
                    <span className="text-[10px] font-semibold text-[var(--text-muted)]">--</span>
                  )}
                </td>
                {/* Gargalo — diagnóstico qualitativo */}
                <td className="px-3 py-2 text-center align-middle">
                  <div className="flex flex-col items-center gap-0.5">
                    <span className={`text-[10px] font-bold ${bn.color}`}>{bn.text}</span>
                    {bottleneckDiagnosis !== '--' && (
                      <span className="text-[9px] text-[var(--text-muted)]">{bottleneckDiagnosis}</span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {members.length === 0 && (
            <tr>
              <td colSpan={9} className="px-3 py-10 text-center">
                <Users size={20} className="mx-auto mb-2 text-[var(--text-muted)] opacity-40" />
                <p className="text-[11px] text-[var(--text-muted)]">Nenhuma tarefa ativa na equipe</p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Activity sort order ──────────────────────────────────────

const STATUS_SORT_ORDER: Record<string, number> = {
  overdue: 0,
  in_progress: 1,
  pending: 2,
  waiting: 3,
};

function activitySortKey(item: UnifiedWorkItem): number {
  if (item.is_overdue) return 0;
  return STATUS_SORT_ORDER[item.status] ?? 4;
}

// ── Source hierarchy colors (border left) ────────────────────

const SOURCE_BORDER: Record<string, string> = {
  problem: 'border-l-red-500',
  workflow: 'border-l-violet-500',
  action: 'border-l-blue-500',
  monitoring: 'border-l-cyan-500',
};

// ── Employee Detail Panel ────────────────────────────────────

function EmployeeDetailPanel({
  userId,
  member,
  items,
  onClose,
}: {
  userId: string;
  member: TeamMemberWorkload;
  items: UnifiedWorkItem[];
  onClose: () => void;
}) {
  const userItems = useMemo(
    () => items
      .filter(i => i.assigned_to === userId && i.status !== 'completed')
      .sort((a, b) => activitySortKey(a) - activitySortKey(b)),
    [items, userId],
  );

  const status = STATUS_STYLES[member.load_status];
  const occupationPct = Math.round((member.total_active / CAPACITY_PER_OPERATOR) * 100);

  return (
    <div className="mt-1 rounded-sm border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-[var(--bg-elevated)] border-b border-[var(--border-default)]">
        <div className="flex items-center gap-3">
          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-[11px] font-bold ${
            isCritical(member) ? 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300' : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] ring-1 ring-[var(--border-default)]'
          }`}>
            {getInitials(member.user_name)}
          </div>
          <div>
            <div className="text-[13px] font-semibold text-[var(--text-primary)]">{member.user_name}</div>
            <div className="text-[9px] text-[var(--text-muted)]">{member.role_label ?? '—'} · Ocupação {occupationPct}%</div>
          </div>
          <span className={`rounded-sm px-1.5 py-0.5 text-[9px] font-bold ${status.bg} ${status.text} ${status.ring}`}>
            {STATUS_LABELS[member.load_status]}
          </span>
          <span className="text-[10px] text-[var(--text-muted)]">{userItems.length} tarefas ativas</span>
          {member.sla_violated_pct > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] font-semibold text-red-600 dark:text-red-400">
              <AlertTriangle size={10} />
              SLA {member.sla_violated_pct}% violado
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            title="Redistribuir tarefas"
            className="flex items-center gap-1 rounded-sm px-2 py-1 text-[9px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--button-hover)] border border-[var(--border-default)]"
          >
            <Shuffle size={10} /> Redistribuir
          </button>
          <button
            title="Priorizar fila"
            className="flex items-center gap-1 rounded-sm px-2 py-1 text-[9px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--button-hover)] border border-[var(--border-default)]"
          >
            <ListFilter size={10} /> Priorizar
          </button>
          <button
            title="Ver gargalo"
            className="flex items-center gap-1 rounded-sm px-2 py-1 text-[9px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--button-hover)] border border-[var(--border-default)]"
          >
            <Eye size={10} /> Gargalo
          </button>
          <button onClick={onClose} className="p-1 rounded-sm hover:bg-[var(--button-hover)] ml-1">
            <X size={14} className="text-[var(--text-muted)]" />
          </button>
        </div>
      </div>

      <div className="px-4 py-3">
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-2">
          Atividades ({userItems.length})
        </h4>
        <div className="space-y-1 max-h-[280px] overflow-y-auto">
          {userItems.map((item) => (
            <div
              key={item.id}
              className={`flex items-center gap-2 rounded-sm border-l-[3px] border px-3 py-1.5 text-[11px] transition-colors ${
                SOURCE_BORDER[item.source] ?? 'border-l-[var(--border-default)]'
              } ${
                item.is_overdue
                  ? 'border-red-200 bg-red-50/60'
                  : item.status === 'in_progress'
                  ? 'border-blue-100 bg-blue-50/40'
                  : 'border-[var(--border-default)] bg-[var(--bg-surface)]'
              }`}
            >
              <span className={`shrink-0 rounded px-1 py-0.5 text-[8px] font-semibold ${WORK_SOURCE_COLORS[item.source]}`}>
                {WORK_SOURCE_LABELS[item.source].slice(0, 3)}
              </span>
              <span className={`shrink-0 rounded px-1 py-0.5 text-[8px] font-semibold ${WORK_STATUS_COLORS[item.status]}`}>
                {WORK_STATUS_LABELS[item.status]}
              </span>
              {item.is_overdue && (
                <span className="shrink-0 rounded bg-red-100 px-1 py-0.5 text-[8px] font-bold text-red-700 dark:bg-red-900/30 dark:text-red-400">ATRASO</span>
              )}
              <span className="min-w-0 flex-1 truncate font-medium text-[var(--text-primary)]">{item.title}</span>
              {item.priority && (
                <span className={`shrink-0 text-[9px] font-mono ${
                  item.priority === 'high' ? 'text-red-500' : item.priority === 'medium' ? 'text-amber-500' : 'text-[var(--text-muted)]'
                }`}>
                  {item.priority === 'high' ? 'ALTA' : item.priority === 'medium' ? 'MED' : 'BAIXA'}
                </span>
              )}
            </div>
          ))}
          {userItems.length === 0 && (
            <p className="text-[10px] text-[var(--text-muted)] italic py-2">Nenhuma tarefa ativa</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────

export const TeamPage: React.FC = () => {
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const organizationId = workspace?.organizationId ?? user?.organization_id ?? user?.organizationId;

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [kpiFilter, setKpiFilter] = useState<KpiFilter>(null);
  const [sortColumn, setSortColumn] = useState<TeamSortColumn>('bottleneck');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [searchValue, setSearchValue] = useState('');
  const [simOpen, setSimOpen] = useState(false);
  const [rebalancePlan, setRebalancePlan] = useState<RebalancePlan | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);

  const handleSortChange = useCallback((col: TeamSortColumn) => {
    setSortColumn(prevCol => {
      if (prevCol === col) {
        setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
        return prevCol;
      }
      setSortDirection(col === 'name' ? 'asc' : 'desc');
      return col;
    });
  }, []);

  // ── Filter dropdown state (portal) ──────────────────────────
  type TeamDd = 'type' | null;
  const [openDd, setOpenDd] = useState<TeamDd>(null);
  const typeBtnRef = useRef<HTMLButtonElement>(null);
  const [ddPos, setDdPos] = useState({ top: 0, left: 0 });

  const toggleDd = useCallback((which: TeamDd) => {
    if (!which) return;
    if (typeBtnRef.current) {
      const r = typeBtnRef.current.getBoundingClientRect();
      setDdPos({ top: r.bottom + 4, left: r.left });
    }
    setOpenDd(prev => (prev === which ? null : which));
  }, []);

  useEffect(() => {
    if (!openDd) return;
    const close = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-team-dd]')) return;
      setOpenDd(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openDd]);

  const teamQuery = useQuery<UnifiedWorkItem[]>({
    queryKey: ['team', organizationId, 'work-items'],
    enabled: Boolean(organizationId),
    staleTime: 30_000,
    queryFn: () => fetchTeamWorkItems(organizationId!),
  });

  const orgMembersQuery = useQuery<UserRow[]>({
    queryKey: ['org-members', organizationId],
    enabled: Boolean(organizationId),
    staleTime: 60_000,
    queryFn: () => organizationStructureService.fetchOrgMembers(organizationId!),
  });

  // Poka-Yoke WIP: wip_max per user from cargo_config
  const wipMaxQuery = useQuery<Map<string, number>>({
    queryKey: ['org-wip-max', organizationId],
    enabled: Boolean(organizationId),
    staleTime: 120_000,
    queryFn: () => getOrgWipMaxByUser(organizationId!),
  });

  const cargoNameQuery = useQuery<Map<string, string>>({
    queryKey: ['org-cargo-name', organizationId],
    enabled: Boolean(organizationId),
    staleTime: 120_000,
    queryFn: () => getOrgCargoNameByUser(organizationId!),
  });

  const allItems = teamQuery.data ?? [];

  const filteredItems = useMemo(() => {
    if (typeFilter === 'all') return allItems;
    return allItems.filter(i => i.source === typeFilter);
  }, [allItems, typeFilter]);

  const membersFromItems = useMemo(() => computeTeamWorkload(filteredItems), [filteredItems]);

  // Merge: all org members appear in table, even those without active tasks
  // Poka-Yoke WIP: override load_status to 'overloaded' when total_active >= cargo.wip_max
  const members = useMemo(() => {
    const orgUsers = orgMembersQuery.data ?? [];
    const wipMaxMap = wipMaxQuery.data;
    const cargoNameMap = cargoNameQuery.data;
    const fromItemsMap = new Map(membersFromItems.map(m => [m.user_id, m]));

    const applyWipOverride = (m: TeamMemberWorkload): TeamMemberWorkload => {
      if (!wipMaxMap) return m;
      const wipMax = wipMaxMap.get(m.user_id);
      if (wipMax != null && m.total_active >= wipMax && m.load_status !== 'overloaded') {
        return { ...m, load_status: 'overloaded' as const };
      }
      return m;
    };

    if (!orgUsers.length) return membersFromItems.map(applyWipOverride);
    const merged: TeamMemberWorkload[] = orgUsers
      .filter(u => u.name)
      .map(u => {
        const existing = fromItemsMap.get(u.id);
        const cargoName = cargoNameMap?.get(u.id);
        const base: TeamMemberWorkload = existing
          ? { ...existing, role_label: cargoName ?? existing.role_label }
          : {
              user_id: u.id,
              user_name: u.name ?? u.email ?? u.id,
              role_label: cargoName ?? undefined,
              total_active: 0,
              tasks_in_progress: 0,
              tasks_pending: 0,
              tasks_overdue: 0,
              tasks_completed_today: 0,
              sla_violated_pct: 0,
              bottleneck_score: 0,
              load_status: 'normal' as const,
            };
        return applyWipOverride(base);
      });
    return merged;
  }, [membersFromItems, orgMembersQuery.data, wipMaxQuery.data, cargoNameQuery.data]);

  const dashboardKPIs = useMemo(() => computeTeamDashboard(filteredItems, members), [filteredItems, members]);

  const displayMembers = useMemo(() => {
    let filtered = members;
    if (kpiFilter === 'overdue') filtered = filtered.filter(m => m.tasks_overdue > 0);
    else if (kpiFilter === 'critical') filtered = filtered.filter(isCritical);
    if (searchValue.trim()) {
      const q = searchValue.trim().toLowerCase();
      filtered = filtered.filter(m => {
        if (m.user_name.toLowerCase().includes(q)) return true;
        const userItems = filteredItems.filter(i => i.assigned_to === m.user_id);
        return userItems.some(i =>
          i.title.toLowerCase().includes(q) ||
          (i.description?.toLowerCase().includes(q)) ||
          WORK_SOURCE_LABELS[i.source].toLowerCase().includes(q)
        );
      });
    }
    return sortMembers(filtered, sortColumn, sortDirection);
  }, [members, kpiFilter, sortColumn, sortDirection, searchValue, filteredItems]);

  const selectedMember = members.find(m => m.user_id === selectedUserId);

  const handleRefresh = useCallback(() => { void teamQuery.refetch(); }, [teamQuery]);

  const handleRebalance = useCallback(() => {
    if (members.length < 2 || allItems.length === 0) return;
    const plan = simulateRebalance(allItems, members);
    setRebalancePlan(plan);
  }, [allItems, members]);

  const handleExecuteRebalance = useCallback(async () => {
    if (!rebalancePlan || rebalancePlan.moves.length === 0 || !organizationId || !user?.id) return;
    setIsExecuting(true);
    try {
      await executeRebalance(rebalancePlan.moves, user.id, organizationId);
      setRebalancePlan(null);
      void teamQuery.refetch();
    } catch (err) {
      console.error('[rebalance] execution error:', err);
    } finally {
      setIsExecuting(false);
    }
  }, [rebalancePlan, organizationId, user?.id, teamQuery]);

  // ── Filter widget (slot for MasterRibbon) ───────────────────
  const activeTypeLabel = TYPE_OPTIONS.find(o => o.value === typeFilter)?.label ?? 'Todos';

  const filtersWidget = useMemo(() => (
    <div className="flex items-center gap-2">
      {/* Type */}
      <button ref={typeBtnRef} data-team-dd="" type="button"
        className="flex h-[30px] shrink-0 items-center gap-1.5 rounded-sm border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]"
        onClick={() => toggleDd('type')}
      >
        <ListFilter size={12} className="text-[var(--text-secondary)]" />
        <span>{activeTypeLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {/* Search */}
      <div className="flex w-[220px] shrink-0 items-center gap-2 rounded-sm border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 h-[30px]">
        <Search size={13} className="shrink-0 text-[var(--text-secondary)]" />
        <input
          type="text"
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          placeholder="Buscar operador, tarefa..."
          className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
        />
        {searchValue && (
          <button onClick={() => setSearchValue('')} className="shrink-0 rounded-sm p-0.5 hover:bg-[var(--button-hover)]">
            <X size={10} className="text-[var(--text-secondary)]" />
          </button>
        )}
      </div>
    </div>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [activeTypeLabel, searchValue, toggleDd]);

  const handleOpenSimulation = useCallback(() => {
    setSimOpen(true);
  }, []);

  const ribbonConfig = useCapacidadeRibbon({
    onRebalance: handleRebalance,
    onPrioritize: () => {},
    onRefresh: handleRefresh,
    onSimulate: handleOpenSimulation,
    canRebalance: members.length >= 2 && allItems.length > 0,
    filtersWidget,
  });

  return (
    <ModuleLayout ribbonConfig={ribbonConfig}>
      {simOpen && (
        <SimulacaoDrawer
          members={members}
          currentWorkload={dashboardKPIs.current_workload}
          onClose={() => setSimOpen(false)}
        />
      )}
      <div className="flex h-full min-h-0 flex-col bg-[var(--bg-canvas)]">
        <div>
          <TeamDashboardCards kpis={dashboardKPIs} members={members} activeFilter={kpiFilter} onFilterClick={setKpiFilter} />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <EmployeeTable
            members={displayMembers}
            selectedUserId={selectedUserId}
            onSelectUser={(uid) => setSelectedUserId(prev => prev === uid ? null : uid)}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
          />

          {selectedUserId && selectedMember && (
            <EmployeeDetailPanel
              userId={selectedUserId}
              member={selectedMember}
              items={filteredItems}
              onClose={() => setSelectedUserId(null)}
            />
          )}
        </div>

        {/* Filter dropdowns — portal rendered */}
        {openDd === 'type' && createPortal(
          <div data-team-dd="" className="ribbon-dropdown-panel absolute z-[9999] min-w-[160px] overflow-hidden rounded-sm border border-[var(--border-default)] bg-[var(--bg-surface)] py-1 shadow-2xl"
            style={{ position: 'fixed', top: ddPos.top, left: ddPos.left }}
          >
            {TYPE_OPTIONS.map(o => (
              <button key={o.value} type="button"
                className="flex w-full items-center gap-2 px-3 py-[6px] text-left text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]"
                onClick={() => { setTypeFilter(o.value); setOpenDd(null); }}
              >
                <span className="flex-1">{o.label}</span>
                <Check size={11} className={typeFilter === o.value ? 'opacity-100' : 'opacity-0'} />
              </button>
            ))}
          </div>,
          document.body
        )}

        {rebalancePlan && (
          <RebalanceModal
            plan={rebalancePlan}
            isExecuting={isExecuting}
            onExecute={handleExecuteRebalance}
            onClose={() => !isExecuting && setRebalancePlan(null)}
          />
        )}

        {(teamQuery.isLoading || orgMembersQuery.isLoading) && (
          <div className="absolute inset-0 flex items-center justify-center bg-[var(--bg-canvas)]/60">
            <p className="text-[13px] text-[var(--text-muted)]">Carregando capacidade...</p>
          </div>
        )}
      </div>
    </ModuleLayout>
  );
};

export default TeamPage;
